import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Breadcrumbs, Button, Card, CardActionArea, CardActions, CardContent, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton,
  Link, Paper, TextField, Typography,
} from "@material-ui/core";
import { Add, Dashboard, DeleteOutline, Edit, Group, Refresh } from "@material-ui/icons";
import Alert from "@material-ui/lab/Alert";
import { useTranslation } from "react-i18next";
import { taskRequest } from "./api";
import BoardMembersDialog from "./BoardMembersDialog";

export default function BoardPicker({ onSelect }) {
  const { t } = useTranslation();
  const [boards, setBoards] = useState([]);
  const [membersBoard, setMembersBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [deletingBoard, setDeletingBoard] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const requestId = useRef(0);
  const mutation = useRef(false);

  const load = useCallback(async () => {
    const current = ++requestId.current;
    setLoading(true);
    setError("");
    try {
      const result = await taskRequest("get_task_boards");
      if (current === requestId.current) setBoards(result.boards);
    } catch (err) {
      if (current === requestId.current) setError(err.message);
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => { requestId.current += 1; };
  }, [load]);

  const saveBoard = async (event) => {
    event.preventDefault();
    if (mutation.current || !name.trim()) return;
    mutation.current = true;
    setSaving(true);
    setSaveError("");
    let board;
    try {
      const result = await taskRequest("save_task_board", {
        name: name.trim(),
        ...(editingBoard ? { boardId: editingBoard.id } : {}),
      });
      board = result.board;
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
      mutation.current = false;
    }
    if (board && editingBoard) {
      setBoards((previous) => previous.map((item) => item.id === board.id ? board : item));
      setEditingBoard(null);
      setCreating(false);
    } else if (board) onSelect(board);
  };

  const deleteBoard = async () => {
    if (mutation.current || !deletingBoard) return;
    mutation.current = true;
    setSaving(true);
    setDeleteError("");
    try {
      await taskRequest("delete_task_board", { boardId: deletingBoard.id });
      setBoards((previous) => previous.filter((board) => board.id !== deletingBoard.id));
      setDeletingBoard(null);
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setSaving(false);
      mutation.current = false;
    }
  };

  return <div style={{ width: "100%", minWidth: 0 }}>
    <Breadcrumbs>
      <Link color="inherit" href="/dashboard">{t("mainSideBar.dashboard")}</Link>
      <Typography>{t("tasks.header")}</Typography>
    </Breadcrumbs>
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, margin: "16px 0" }}>
      <Typography component="h1" variant="h5" style={{ flexGrow: 1 }}>{t("tasks.boards")}</Typography>
      <IconButton aria-label={t("tasks.refreshBoards")} disabled={loading || saving} onClick={load}><Refresh /></IconButton>
      <Button color="primary" variant="contained" startIcon={<Add />} disabled={loading || saving} onClick={() => {
        setName(""); setSaveError(""); setEditingBoard(null); setCreating(true);
      }}>{t("tasks.createBoard")}</Button>
    </div>
    {error && <Alert severity="error" role="alert" style={{ marginBottom: 16 }}>{error}</Alert>}
    {loading ? <CircularProgress aria-label={t("tasks.loadingBoards")} /> : <>
      {!boards.length && !error && <Paper style={{ padding: 32, textAlign: "center" }}>
        <Typography color="textSecondary">{t("tasks.emptyBoards")}</Typography>
      </Paper>}
      {!!boards.length && <>
        <Typography color="textSecondary" paragraph>{t("tasks.selectBoard")}</Typography>
        <Grid container spacing={2}>
          {boards.map((board) => <Grid item xs={12} sm={6} md={4} lg={3} key={board.id}>
            <Card>
              <CardActionArea disabled={saving} onClick={() => onSelect(board)}>
                <CardContent style={{ minHeight: 100 }}>
                  <Dashboard color="primary" />
                  <Typography component="h2" variant="h6" style={{ overflowWrap: "anywhere" }}>{board.name}</Typography>
                </CardContent>
              </CardActionArea>
              <CardActions style={{ flexWrap: "wrap" }}>
                <Button size="small" startIcon={<Group />} disabled={saving}
                  aria-label={`${t("tasks.members")}: ${board.name}`}
                  onClick={() => setMembersBoard(board)}>{t("tasks.members")}</Button>
                <Button size="small" startIcon={<Edit />} disabled={saving}
                  aria-label={`${t("tasks.renameBoard")}: ${board.name}`}
                  onClick={() => {
                    setName(board.name); setSaveError(""); setEditingBoard(board); setCreating(true);
                  }}>{t("tasks.renameBoard")}</Button>
                <Button size="small" startIcon={<DeleteOutline />} disabled={saving}
                  aria-label={`${t("tasks.deleteBoard")}: ${board.name}`}
                  onClick={() => { setDeleteError(""); setDeletingBoard(board); }}>{t("tasks.deleteBoard")}</Button>
              </CardActions>
            </Card>
          </Grid>)}
        </Grid>
      </>}
    </>}
    {membersBoard && <BoardMembersDialog board={membersBoard} onClose={() => setMembersBoard(null)} />}
    <Dialog open={creating} onClose={() => { if (!saving) setCreating(false); }} fullWidth maxWidth="xs" aria-labelledby="create-board-title">
      <form onSubmit={saveBoard}>
        <DialogTitle id="create-board-title">{t(editingBoard ? "tasks.renameBoard" : "tasks.createBoard")}</DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error" role="alert">{saveError}</Alert>}
          {!editingBoard && <Typography paragraph>{t("tasks.creatorMember")}</Typography>}
          <TextField id="task-board-name" autoFocus required fullWidth label={t("tasks.boardName")} value={name} disabled={saving}
            inputProps={{ maxLength: 255 }} onChange={(event) => setName(event.target.value)} />
        </DialogContent>
        <DialogActions>
          {saving && <CircularProgress size={20} />}
          <Button disabled={saving} onClick={() => setCreating(false)}>{t("tasks.close")}</Button>
          <Button type="submit" color="primary" disabled={saving || !name.trim()}>{t(editingBoard ? "tasks.save" : "tasks.createBoard")}</Button>
        </DialogActions>
      </form>
    </Dialog>
    <Dialog open={!!deletingBoard} onClose={() => { if (!saving) setDeletingBoard(null); }}
      fullWidth maxWidth="sm" aria-labelledby="delete-board-title" aria-describedby="delete-board-description">
      <DialogTitle id="delete-board-title">{t("tasks.deleteBoard")}</DialogTitle>
      <DialogContent>
        {deleteError && <Alert severity="error" role="alert">{deleteError}</Alert>}
        <Typography id="delete-board-description">{t("tasks.deleteBoardConfirmation", { name: deletingBoard ? deletingBoard.name : "" })}</Typography>
      </DialogContent>
      <DialogActions>
        {saving && <CircularProgress size={20} />}
        <Button disabled={saving} onClick={() => setDeletingBoard(null)}>{t("tasks.cancel")}</Button>
        <Button color="secondary" variant="contained" disabled={saving} onClick={deleteBoard}>{t("tasks.confirmDeleteBoard")}</Button>
      </DialogActions>
    </Dialog>
  </div>;
}
